import atividadeService from "@/service/atividadeService";
import { usePagination } from "@/composables/usePagination";
import { ref } from "vue";

export function useAtividades() {
  const atividades = ref([]);
  const atividade = ref(null);
  const participantes = ref([]);
  const grupo = ref(null);
  const tiposSubtipos = ref([]);
  const subtipo = ref(null);
  const isLoading = ref(false);
  const isSaving = ref(false);

  const pagination = usePagination({
    initialPage: 0,
    pageSize: 10,
    baseIndex: 0,
  });

  const fetchAtividades = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    filtros = null
  ) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getAtividades(
        page,
        size,
        true,
        filtros
      );
      if (response) {
        atividades.value = response.atividades || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao listar atividades:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAtividadesAcao = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    acaoId,
    filtros = null
  ) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getAtividadesAcao(
        page,
        size,
        acaoId,
        true,
        filtros
      );
      if (response) {
        atividades.value = response.atividades || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao listar atividades por ação:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAtividadesComFiltros = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    filtros = {},
    acaoId = null
  ) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getAtividadesComFiltros(
        page,
        size,
        filtros,
        acaoId
      );
      if (response) {
        atividades.value = response.content || response.data || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao buscar filtros de atividades:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAtividadesPorPalavraChave = async (
    palavra,
    page = pagination.currentPage.value,
    size = pagination.pageSize.value
  ) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getAtividadesPorPalavraChave(
        palavra,
        page,
        size
      );
      if (response) {
        atividades.value = response.content || response.data || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao buscar atividades por palavra-chave:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAtividadesUnidade = async (
    page = pagination.currentPage.value,
    size = pagination.pageSize.value,
    unidadeId
  ) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getAtividadesUnidade(
        page,
        size,
        unidadeId
      );
      if (response) {
        atividades.value = response.content || [];
        pagination.setPaginationInfo(response);
        pagination.currentPage.value = page;
      }
      return response;
    } catch (error) {
      console.error("Erro ao listar atividades por unidade:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchAtividadeById = async (id) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getAtividadeId(id);
      atividade.value = response;
      return response;
    } catch (error) {
      console.error("Erro ao buscar atividade por ID:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const saveAtividade = async (dadosAtividade) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.postAtividade(dadosAtividade);
      return response;
    } catch (error) {
      console.error("Erro ao criar atividade:", error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const updateAtividade = async (atividadeId, dadosAtividade) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.putAtividade(
        atividadeId,
        dadosAtividade
      );
      return response;
    } catch (error) {
      console.error("Erro ao editar atividade:", error);
      throw error;
    } finally {
      isSaving.value = false;
    }
  };

  const deleteAtividade = async (atividadeId) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.deletAtividade(atividadeId);
      return response;
    } catch (error) {
      console.error("Erro ao deletar atividade:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const cancelAtividade = async (atividadeId, motivo) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.cancelarAtividade(
        atividadeId,
        motivo
      );
      return response;
    } catch (error) {
      console.error("Erro ao cancelar atividade:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const encerrarAtividade = async (atividadeId) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.encerrarAtividade(atividadeId);
      return response;
    } catch (error) {
      console.error("Erro ao encerrar atividade:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const concluirAtividade = async (atividadeId) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.concluirAtividade(atividadeId);
      return response;
    } catch (error) {
      console.error("Erro ao concluir atividade:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchTiposeSubtipos = async () => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getTiposeSubtipos();
      tiposSubtipos.value = response || [];
      return response;
    } catch (error) {
      console.error("Erro ao buscar tipos e subtipos:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchSubTipoPorId = async (id) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getSubTipoPorId(id);
      subtipo.value = response;
      return response;
    } catch (error) {
      console.error("Erro ao buscar subtipo por ID:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const fetchParticipantesAtividade = async (atividadeId) => {
    isLoading.value = true;
    try {
      const response =
        await atividadeService.getParticipantesAtividade(atividadeId);
      participantes.value = response || [];
      return response;
    } catch (error) {
      console.error("Erro ao listar participantes da atividade:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const escolherParticipantesAtividade = async (
    atividadeId,
    dadosParticipantes
  ) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.escolherParticipantesAtividade(
        atividadeId,
        dadosParticipantes
      );
      return response;
    } catch (error) {
      console.error("Erro ao escolher participantes da atividade:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const cadastrarGrupoAtividade = async (atividadeId, membrosGrupo) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.cadastrarGrupoAtividade(
        atividadeId,
        membrosGrupo
      );
      return response;
    } catch (error) {
      console.error("Erro ao criar grupo da atividade:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const adicionarMembroGrupo = async (atividadeId, membrosGrupo) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.adicionarMembroGrupo(
        atividadeId,
        membrosGrupo
      );
      return response;
    } catch (error) {
      console.error("Erro ao adicionar membro ao grupo:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const removerMembroGrupo = async (atividadeId, membrosGrupo) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.removerMembroGrupo(
        atividadeId,
        membrosGrupo
      );
      return response;
    } catch (error) {
      console.error("Erro ao remover membro do grupo:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const fetchGrupoAtividade = async (atividadeId) => {
    isLoading.value = true;
    try {
      const response = await atividadeService.getGrupoAtividade(atividadeId);
      grupo.value = response;
      return response;
    } catch (error) {
      console.error("Erro ao obter grupo da atividade:", error);
    } finally {
      isLoading.value = false;
    }
  };

  const promoverMembroEquipe = async (atividadeId, dadosMembroTime) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.promoverMembroEquipe(
        atividadeId,
        dadosMembroTime
      );
      return response;
    } catch (error) {
      console.error("Erro ao promover membro da equipe:", error);
    } finally {
      isSaving.value = false;
    }
  };

  const rebaixarMembroEquipe = async (atividadeId, dadosMembroTime) => {
    isSaving.value = true;
    try {
      const response = await atividadeService.rebaixarMembroEquipe(
        atividadeId,
        dadosMembroTime
      );
      return response;
    } catch (error) {
      console.error("Erro ao rebaixar membro da equipe:", error);
    } finally {
      isSaving.value = false;
    }
  };

  return {
    atividades,
    atividade,
    participantes,
    grupo,
    tiposSubtipos,
    subtipo,
    isLoading,
    isLoadingAtividade: isLoading,
    isSaving,
    isSavingAtividade: isSaving,
    currentPage: pagination.currentPage,
    totalPages: pagination.totalPages,
    totalAtividades: pagination.totalElements,
    pageSize: pagination.pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    fetchAtividades,
    fetchAtividadesAcao,
    fetchAtividadesComFiltros,
    fetchAtividadesPorPalavraChave,
    fetchAtividadesUnidade,
    fetchAtividadeById,
    saveAtividade,
    updateAtividade,
    deleteAtividade,
    cancelAtividade,
    encerrarAtividade,
    concluirAtividade,
    fetchTiposeSubtipos,
    fetchSubTipoPorId,
    fetchParticipantesAtividade,
    escolherParticipantesAtividade,
    cadastrarGrupoAtividade,
    adicionarMembroGrupo,
    removerMembroGrupo,
    fetchGrupoAtividade,
    promoverMembroEquipe,
    rebaixarMembroEquipe,
    nextPage: pagination.nextPage,
    previousPage: pagination.previousPage,
    goToPage: pagination.goToPage,
  };
}
